import React from "react";
import { Stack, Box, Typography, CircularProgress } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { FONT_FAMILY } from "@/utils/font-family";
import { MessageDateTimeComponent } from "@/widgets/messenger/components/message-date-time-component";
import type { AISuggestionListItem } from "@/services/frontdesk/api/suggestion.service";

interface AiProcessedNotificationItemProps {
  item: AISuggestionListItem;
  onClick: (item: AISuggestionListItem) => void;
}

const AiProcessedNotificationItem: React.FC<AiProcessedNotificationItemProps> = ({
  item,
  onClick,
}) => {
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const { primaryColor, neutralColor } = useGomakeTheme();
  const isProcessing = item.status === "processing" || item.status === "queued";
  const title = item.subject || "Email";
  const preview =
    item.suggestionPreview ||
    (isProcessing ? "AI is preparing a response…" : "AI response ready");

  return (
    <Stack
      direction="row"
      gap="12px"
      padding="12px 20px"
      dir={dir}
      sx={{
        cursor: "pointer",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        boxSizing: "border-box",
        direction: dir,
        textAlign: "start",
        "&:hover": {
          backgroundColor: neutralColor(50),
        },
        borderBottom: `1px solid ${neutralColor(100)}`,
      }}
      onClick={() => onClick(item)}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isProcessing ? "#F3E8FF" : "#ECFDF5",
        }}
      >
        {isProcessing ? (
          <CircularProgress size={18} thickness={5} sx={{ color: "#7C3AED" }} />
        ) : (
          <CheckCircleIcon sx={{ fontSize: 22, color: "#10B981" }} />
        )}
      </Box>

      <Stack flex={1} gap="4px" sx={{ minWidth: 0, overflow: "hidden", direction: dir }}>
        <Stack
          direction="row"
          alignItems="center"
          gap="8px"
          sx={{ minWidth: 0, direction: dir }}
        >
          <Typography
            sx={{
              ...FONT_FAMILY.Lexend(600, 14),
              color: primaryColor(900),
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              flex: 1,
              textAlign: "start",
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "2px 8px",
              borderRadius: "4px",
              backgroundColor: isProcessing ? "#F3E8FF" : "#ECFDF5",
              flexShrink: 0,
            }}
          >
            <AutoAwesomeOutlinedIcon
              sx={{ fontSize: 12, color: isProcessing ? "#7C3AED" : "#10B981" }}
            />
            <Typography
              sx={{
                ...FONT_FAMILY.Lexend(500, 11),
                color: isProcessing ? "#7C3AED" : "#059669",
              }}
            >
              {isProcessing ? "Processing" : "Ready"}
            </Typography>
          </Box>
        </Stack>

        <Typography
          sx={{
            ...FONT_FAMILY.Lexend(400, 13),
            color: neutralColor(600),
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
            textAlign: "start",
          }}
        >
          {preview}
        </Typography>

        <Box sx={{ color: neutralColor(400), minWidth: 0, textAlign: "start" }}>
          <MessageDateTimeComponent
            date={new Date(item.completedAt || item.createdAt || Date.now())}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

export { AiProcessedNotificationItem };
